<template>
  <lila-default-template>
    <template #mainContent>
      <div class="notifications-page">
        <div class="page-header">
          <div class="header-content">
            <div class="title-wrapper">
              <h1 class="page-title">
                Notificações
              </h1>
            </div>
            <p class="page-subtitle">
              Acompanhe todas as suas notificações.
            </p>
          </div>

          <div
            v-if="allNotifications.length > 0"
            class="header-actions"
          >
            <lila-button
              type="button"
              title="Marcar todas como lidas"
              :is-disabled="unreadCount === 0"
              button-style="default"
              :loading="false"
              @custom-click-event="markAllAsRead"
            />

            <lila-button
              type="button"
              title="Limpar todas"
              class="btn-outlined"
              button-style="button is-rounded cancel"
              :loading="false"
              @custom-click-event="clearAllNotifications"
            />
          </div>
        </div>

        <div
          v-if="isLoading"
          class="loading-state"
        >
          <div class="spinner" />
          <p class="loading-text">
            Carregando notificações...
          </p>
        </div>

        <transition-group
          v-else-if="allNotifications.length > 0"
          name="notification-list"
          tag="div"
          class="notifications-container"
        >
          <article
            v-for="notification in paginatedNotifications"
            :key="notification.Id"
            class="notification-card"
            :class="[
              { 'is-unread': !notification.IsRead },
              getStatusClass(notification.DueDate)
            ]"
            role="button"
            tabindex="0"
            @click="handleNotificationClick(notification)"
            @keydown.enter="handleNotificationClick(notification)"
            @keydown.space.prevent="handleNotificationClick(notification)"
          >
            <div class="notification-body">
              <header class="notification-header">
                <div class="header-left">
                  <span
                    class="status-dot-left"
                    :class="getStatusClass(notification.DueDate)"
                  />
                  <h3 class="notification-title">
                    {{ notification.Title }}
                  </h3>
                </div>
                <time
                  class="notification-time"
                  :datetime="notification.DueDate"
                >
                  {{ notification.TimeAgo }}
                </time>
              </header>

              <p class="notification-message">
                {{ notification.Message }}
              </p>

              <div
                class="notification-footer"
                :class="{
                  'is-unread': !notification.IsRead
                }"
              >
                <div class="meta-grid">
                  <div class="meta-item">
                    <span class="meta-label">Paciente:</span>
                    <span class="meta-value">{{
                      notification.PatientName
                    }}</span>
                  </div>

                  <div class="meta-item priority">
                    <span class="meta-label">Prazo:</span>
                    <span
                      class="meta-value deadline"
                      :class="getStatusClass(notification.DueDate)"
                    >
                      {{ formatDate(notification.DueDate) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </transition-group>

        <div
          v-else
          class="empty-state"
        >
          <p class="empty-title">
            Nenhuma notificação
          </p>
          <p class="empty-description">
            Quando houverem demandas que precisam da sua atenção, elas
            aparecerão aqui
          </p>
        </div>

        <lila-pagination
          v-if="allNotifications.length > 0 && paginationConfig.totalPages > 1"
          :pagination-config="paginationConfig"
          @custom-change-page-event="changePage"
        />
      </div>
    </template>
  </lila-default-template>
</template>

<script>
import LilaDefaultTemplate from "@/components/template/DefaultTemplate.vue";
import LilaPagination from "@/components/shared/pagination/Pagination.vue";
import LilaButton from "@/components/shared/button/Button.vue";

export default {
  name: "NotificationsPage",
  components: {
    LilaDefaultTemplate,
    "lila-button": LilaButton,
    "lila-pagination": LilaPagination
  },

  data() {
    return {
      paginationConfig: {
        current: 1,
        totalPages: 0,
        perPage: 10
      },
      notifications: [],
      isLoading: false
    };
  },

  computed: {
    paginatedNotifications() {
      const start =
        (this.paginationConfig.current - 1) * this.paginationConfig.perPage;
      const end = start + this.paginationConfig.perPage;
      return this.allNotifications.slice(start, end);
    },

    allNotifications() {
      return this.notifications;
    },

    unreadCount() {
      return this.notifications.filter((n) => !n.IsRead).length;
    }
  },

  created() {
    this.getNotifications();
  },

  methods: {
    changePage(page) {
      this.paginationConfig.current = page;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    async getNotifications() {
      this.isLoading = true;
      this.paginationConfig.current = 1;
      this.paginationConfig.totalPages = 0;

      try {
        const res = await this.$axios.get("/api/notifications", {
          headers: {
            Authorization: `bearer ${
              this.$Session.get(this.$userSessionName).token.Value
            }`
          }
        });

        this.notifications = res.data;

        this.paginationConfig.totalPages = Math.ceil(
          this.notifications.length / this.paginationConfig.perPage
        );
      } catch (error) {
        this.$Utilities.verifyToken(error.response.status);
        console.error("Erro ao carregar notificações:", error);
      } finally {
        this.isLoading = false;
      }
    },

    getStatusClass(dueDate) {
      const now = new Date();
      const due = new Date(dueDate);
      const diffDays = Math.ceil(
        (due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (diffDays < 0) return "status-overdue";
      if (diffDays <= 1) return "status-critical";
      if (diffDays <= 3) return "status-warning";
      if (diffDays <= 7) return "status-attention";
      return "status-ok";
    },

    formatDate(date) {
      return this.$Utilities.formatDate(
        date,
        "YYYY-MM-DDTHH:mm:ss",
        "DD/MM/YYYY"
      );
    },

    async handleNotificationClick(notification) {
      if (!notification.IsRead) {
        await this.markAsRead(notification.Id);
      }

      await this.$router.push({
        name: "myPatientActions",
        params: {
          id: notification.PatientId
        }
      });

      window.location.reload();
    },

    async markAsRead(id) {
      const index = this.notifications.findIndex(
        (notification) => notification.Id === id
      );

      if (index === -1) return;

      this.notifications[index].IsRead = true;

      try {
        await this.$axios.put("/api/notifications/mark-as-read", [id], {
          headers: {
            Authorization: `bearer ${
              this.$Session.get(this.$userSessionName).token.Value
            }`
          }
        });
      } catch (error) {
        this.notifications[index].IsRead = false;
      }
    },

    markAllAsRead() {
      this.notifications.forEach((n) => (n.IsRead = true));

      this.$axios
        .put(
          "/api/notifications/mark-all-as-read",
          {},
          {
            headers: {
              Authorization: `bearer ${
                this.$Session.get(this.$userSessionName).token.Value
              }`
            }
          }
        )
        .catch((error) => {
          console.error("Erro ao marcar todas como lidas:", error);
        });
    },

    async clearAllNotifications() {
      try {
        await this.$axios.delete("/api/notifications/delete-all", {
          headers: {
            Authorization: `bearer ${
              this.$Session.get(this.$userSessionName).token.Value
            }`
          }
        });

        this.notifications = [];
        await this.getNotifications();
      } catch (error) {
        this.$Utilities.verifyToken(error.response?.status);
      }
    }
  }
};
</script>

<style scoped>
.notifications-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  min-height: calc(100vh - 80px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 32px;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 2px solid #f0f0f0;
}

.header-content {
  flex: 1;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.page-title {
  margin: 0;
  font-weight: 700;
  font-size: 3.6em !important;
  color: #4f3f82;
  letter-spacing: -0.5px;
}

.page-subtitle {
  margin: 0;
  font-size: 15px;
  color: #797979;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  gap: 5px;
  flex-shrink: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #9a397d;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  margin: 0;
  font-size: 15px;
  color: #797979;
  font-weight: 500;
}

.notifications-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
}

.notification-card {
  display: flex;
  gap: 0;
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.notification-card.is-unread {
  background: rgba(231, 170, 213, 0.03);
  border: 1px solid #9c6c8e;
}

.notification-body {
  flex: 1;
  padding: 20px 24px;
  min-width: 0;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.status-dot-left {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 15px;
}

.status-dot-left.status-overdue {
  background: #db4545;
  box-shadow: 0 0 0 3px rgba(219, 69, 69, 0.2);
}

.status-dot-left.status-critical {
  background: #ff6b6b;
  box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.2);
}

.status-dot-left.status-warning {
  background: #dfc12a;
  box-shadow: 0 0 0 3px rgba(223, 193, 42, 0.2);
}

.status-dot-left.status-attention {
  background: #f39c12;
  box-shadow: 0 0 0 3px rgba(243, 156, 18, 0.2);
}

.status-dot-left.status-ok {
  background: #35a29d;
  box-shadow: 0 0 0 3px rgba(53, 162, 157, 0.2);
}

.notification-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #4f3f82;
}

.notification-time {
  flex-shrink: 0;
  font-size: 13px;
  color: #4e4e4e;
  font-weight: 500;
}

.notification-message {
  margin: 0 0 16px 0;
  font-size: 14px;
  line-height: 1.6;
  color: #484848;
}

.notification-footer {
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.notification-footer.is-unread {
  padding-top: 16px;
  border-top: 1px solid #d8d7d8;
}

.meta-grid {
  display: grid;
  gap: 12px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.meta-label {
  color: #797979;
  font-weight: 500;
}

.meta-value {
  color: #4f3f82;
  font-weight: 600;
}

.meta-item.priority {
  flex-wrap: wrap;
}

.meta-value.deadline {
  font-weight: 700;
}

.meta-value.deadline.status-overdue,
.meta-value.deadline.status-critical {
  color: #db4545;
}

.meta-value.deadline.status-warning {
  color: #dfc12a;
}

.meta-value.deadline.status-attention {
  color: #f39c12;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-title {
  margin: 0 0 12px 0;
  font-size: 22px;
  font-weight: 700;
  color: #4f3f82;
  letter-spacing: -0.5px;
}

.empty-description {
  margin: 0;
  font-size: 15px;
  color: #797979;
  line-height: 1.6;
  max-width: 400px;
}

.notification-list-enter-active,
.notification-list-leave-active {
  transition: all 0.3s ease;
}

.notification-list-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.notification-list-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}
</style>
